export type ExtendedWarrantyPriceCalculationInput = {
  warrantedQuantity: number;
  warrantyDuration: number;
  warrantyPrice: number;
};

type RoundMethod = 'round' | 'ceil' | 'floor';

const decimalAdjust: any = (type: RoundMethod, value: any, exp: any) => {
  // If the exp is undefined or zero...
  if (typeof exp === 'undefined' || +exp === 0) {
    return Math[type](value);
  }

  // If the value is not a number or the exp is not an integer...
  if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
    return NaN;
  }
  // If the value is negative...
  if (value < 0) {
    return -decimalAdjust(type, -value, exp);
  }
  // Shift
  value = value.toString().split('e');
  value = Math[type](+(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp)));
  // Shift back
  value = value.toString().split('e');

  return +(value[0] + 'e' + (value[1] ? +value[1] + exp : exp));
};

/**
 * Rounds the number to given decimal places
 *
 * @example 1055.004999999999999999999909090 would be 1055.01
 *
 * @param  {Number} price         Price to round
 * @param  {String} action        'round', 'floor', 'ceil'
 * @param  {Number} decimalPlaces Number of decimal places to keep (default: 2)
 * @param  {Number} maxDecimal    Max decimal places to consider (default: 2)
 * @return {Number}               Rounded number
 */
const roundPrice = (price: any, action = 'round', decimalPlaces = 2, maxDecimal = 20) => {
  const priceIsInvalid = ['number', 'string'].indexOf(typeof price) === -1;

  // Price is invalid or has no decimal portion
  if (priceIsInvalid || price % 1 === 0) {
    return parseFloat(price);
  }

  const priceString = price.toString();
  const decimalPlacesString = priceString.substr(priceString.indexOf('.'), maxDecimal + 1);
  const minDecimalPlaces = decimalPlaces - 1;

  let decimalPlacesNumber = +decimalPlacesString;
  let decimalDigits = decimalPlacesString.length - 1;

  for (; decimalDigits > minDecimalPlaces; decimalDigits--) {
    decimalPlacesNumber = decimalAdjust(action, decimalPlacesNumber, -decimalDigits);
  }

  // If number starts with -0, we need to skip parseInt, as it will convert it to 0 instead of -0
  const adjustedPrice: any = price > -1 && price < 0 ? '-0' : parseInt(price, 10);

  //If decimalPlacesNumber === 1 then it means the rounding has rounded the value from probably .99... to 1
  //Which means we need ot increase the integer part by one
  if (decimalPlacesNumber === 1) {
    return price < 0 ? adjustedPrice - 1 : adjustedPrice + 1;
  }

  // JS tweak to avoid floating point errors
  return parseFloat(`${adjustedPrice}.${decimalPlacesNumber.toString().substr(2)}`);
};

export const calculatePrice = ({
  warrantedQuantity,
  warrantyDuration,
  warrantyPrice,
}: ExtendedWarrantyPriceCalculationInput): number => {
  // Warranty Price is monthly so we have to multiply by 12
  return roundPrice(warrantedQuantity * warrantyDuration * warrantyPrice * 12);
};
